import React, { useMemo } from "react";
import {
  HABITAT_COLORS,
  type GameState,
  type Habitat,
  type Season,
  type Tile,
  type ViewMode,
} from "../../vowGameLogic";
import { ACTION_ICONS, TILE_STATUS_ICONS } from "../iconAssets";

type GameTileProps = {
  tile: Tile;
  index: number;
  isSelected: boolean;
  gameState: GameState;
  viewMode: ViewMode;
  selectedSeason: Season;
  canPerformActions: boolean;
  selectedOverlayMax: number;
  onTileClick: (index: number) => void;
};

const ACTION_ICON_KEYS = ["graze", "harvest", "hunt"] as const;

const POTENTIAL_BASE_RGB = "79, 70, 229";
const POTENTIAL_MIN_ALPHA = 0.1;
const POTENTIAL_MAX_ALPHA = 1;

const clamp01 = (value: number): number => {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
};

const GameTile: React.FC<GameTileProps> = ({
  tile,
  index,
  isSelected,
  gameState,
  viewMode,
  selectedSeason,
  canPerformActions,
  selectedOverlayMax,
  onTileClick,
}) => {
  const tileAppearance = useMemo(() => {
    const defaultBackground = HABITAT_COLORS[tile.habitat as Habitat];
    let overlayBackgroundColor = "transparent";
    let tileBackgroundColor = defaultBackground;
    let textColor = "#1f2937";
    let text = "";

    if (viewMode === "water_level") {
      const waterLevel = tile.resources[`waterLevel${selectedSeason}`];
      if (waterLevel === undefined) {
        return {
          text: "N/A",
          overlayBackgroundColor,
          tileBackgroundColor,
          textColor,
        };
      }

      if (waterLevel > 20) overlayBackgroundColor = "#3b82f6";
      else if (waterLevel > 0) overlayBackgroundColor = "#93c5fd";
      else if (waterLevel > -20) overlayBackgroundColor = "#a87b53";
      else overlayBackgroundColor = "#6b4e33";

      return {
        text: `${waterLevel}cm`,
        overlayBackgroundColor,
        tileBackgroundColor,
        textColor,
      };
    }

    if (viewMode === "habitat") {
      text = `${tile.id.split("-")[1]}\n${tile.altitude}cm`;
      return { text, overlayBackgroundColor, tileBackgroundColor, textColor };
    }

    const value = tile.resources[viewMode] || 0;
    const normalized =
      selectedOverlayMax > 0 ? clamp01(value / selectedOverlayMax) : 0;
    const alpha =
      POTENTIAL_MIN_ALPHA +
      (POTENTIAL_MAX_ALPHA - POTENTIAL_MIN_ALPHA) * normalized;
    tileBackgroundColor = `rgba(${POTENTIAL_BASE_RGB}, ${alpha.toFixed(3)})`;
    text = value > 0 ? `${Math.round(value)}` : "";
    textColor = normalized > 0.55 ? "#F3F0E6" : "#1f2937";

    return {
      text,
      overlayBackgroundColor,
      tileBackgroundColor,
      textColor,
    };
  }, [selectedOverlayMax, selectedSeason, tile, viewMode]);

  const tileIconClass =
    "object-contain drop-shadow-sm h-[clamp(1.5rem,4vw,3rem)] w-[clamp(1.5rem,4vw,3rem)]";

  return (
    <button
      type="button"
      onClick={() => onTileClick(index)}
      className={`relative flex aspect-square w-full flex-col items-center justify-center p-1 text-center text-[10px] font-semibold transition-transform`}
      disabled={!canPerformActions || gameState.phase !== "actions"}
      style={{
        backgroundColor: tileAppearance.tileBackgroundColor,
        color: tileAppearance.textColor,
      }}
    >

      <div
        className="absolute inset-0 flex items-center justify-center text-xs font-bold"
        style={{
          backgroundColor: tileAppearance.overlayBackgroundColor ?? "transparent",
          color: tileAppearance.textColor,
          mixBlendMode: "normal",
        }}
      >
        {tileAppearance.text}
      </div>
      {gameState.annee <= tile.protectedUntilYear && (
        <img
          src={TILE_STATUS_ICONS.protected.src}
          alt={TILE_STATUS_ICONS.protected.alt}
          className={`absolute top-1 right-1 ${tileIconClass}`}
        />
      )}
      {tile.hasNest && (
        <img
          src={TILE_STATUS_ICONS.nest.src}
          alt={TILE_STATUS_ICONS.nest.alt}
          className={`absolute top-1 left-1 ${tileIconClass}`}
        />
      )}
      {tile.nestSuccessLastAct && (
        <img
          src={TILE_STATUS_ICONS.nestSuccess.src}
          alt={TILE_STATUS_ICONS.nestSuccess.alt}
          className={`absolute bottom-1 right-1 ${tileIconClass}`}
        />
      )}
      {(tile.actions.graze || tile.actions.harvest || tile.actions.hunt) && (
        <div className="absolute bottom-1 left-1 flex items-center gap-1">
          {ACTION_ICON_KEYS.filter((key) => tile.actions[key]).map((key) => {
            const icon = ACTION_ICONS[key];
            return (
              <img
                key={icon.src}
                src={icon.src}
                alt={icon.alt}
                className={tileIconClass}
              />
            );
          })}
        </div>
      )}
      <div className={`absolute left-0 top-0 w-full h-full ${
        isSelected ? "border-dashed border-4 border-black" : ""
      }`}/>
    </button>
  );
};

export default GameTile;
